import { employeeServiceApi } from "../../app/employeeServiceApi";
import { API_URL } from "../../constants/apiUrl";
import {
  GetApiTokenType,
  GetDeviceAllocatedParamsType,
  GetDeviceAllocatedType,
  GetDeviceListParams,
  GetDeviceListType,
  GetDeviceUnAllocatedParamsType,
  GetDeviceUnAllocatedType,
  GetQueryArgsType,
} from "../../types";

export const deviceApi = employeeServiceApi.injectEndpoints({
  endpoints: (builder) => ({
    getDeviceList: builder.query<
      GetDeviceListType,
      GetQueryArgsType<GetDeviceListParams>
    >({
      query: (queryArgs) => ({
        url: API_URL.getDeviceList,
        ...queryArgs,
      }),
      providesTags: ["GetDeviceList"],
    }),
    getDeviceAllocated: builder.query<
      GetDeviceAllocatedType,
      GetDeviceAllocatedParamsType
    >({
      query: (params) => ({
        url: `${API_URL.getDeviceAllocated}/${String(params?.id)}`,
        params: {
          skip: params?.skip,
          limit: params?.limit,
          search_type: params?.search_type,
          search: params?.search,
          status: params?.status,
        },
      }),
      providesTags: (_, __, params) => [
        { type: "GetDeviceAllocated", id: params?.id },
      ],
    }),
    getDeviceUnAllocated: builder.query<
      GetDeviceUnAllocatedType,
      GetDeviceUnAllocatedParamsType
    >({
      query: (params) => ({
        url: `${API_URL.getDeviceUnAllocated}/${String(params?.id)}`,
        params: {
          skip: params?.skip,
          limit: params?.limit,
          search_type: params?.search_type,
          search: params?.search,
          status: params?.status,
        },
      }),
      providesTags: (_, __, params) => [
        { type: "GetDeviceUnAllocated", id: params?.id },
      ],
    }),
    revokeUserFromDevice: builder.mutation<unknown, { data: any; id: number }>({
      query: ({ data, id }) => ({
        url: `${API_URL.revokeUserFromDevice}/${id}`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result, _, { id }) => {
        if (result)
          return [
            {
              type: "GetDeviceAllocated",
              id,
            },
            {
              type: "GetDeviceUnAllocated",
              id,
            },
          ];

        return [];
      },
    }),
    allocatedUserInDevice: builder.mutation<unknown, { data: any; id: number }>(
      {
        query: ({ data, id }) => ({
          url: `${API_URL.allocatedUserInDevice}/${id}`,
          method: "POST",
          body: data,
        }),
        invalidatesTags: (result, _, { id }) => {
          if (result)
            return [
              {
                type: "GetDeviceAllocated",
                id,
              },
              {
                type: "GetDeviceUnAllocated",
                id,
              },
            ];

          return [];
        },
      }
    ),
    startEnrollmentInDevice: builder.mutation<
      unknown,
      { data: any; id: number }
    >({
      query: ({ data, id }) => ({
        url: `${API_URL.startEnrollmentInDevice}/${id}`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result, _, { data }) => {
        if (result)
          return [
            {
              type: "GetDeviceAllocated",
              id: data?.deviceIdentifier,
            },
            {
              type: "GetDeviceUnAllocated",
              id: data?.deviceIdentifier,
            },
          ];

        return [];
      },
    }),

    stopEnrollmentDevice: builder.mutation<unknown, { id: number }>({
      query: ({ id }) => ({
        url: `${API_URL.stopEnrollmentDevice}/${id}`,
        method: "POST",
      }),
      invalidatesTags: (result, _) => {
        if (result)
          return [
            {
              type: "GetDeviceAllocated",
            },
            {
              type: "GetDeviceUnAllocated",
            },
          ];

        return [];
      },
    }),

    createBulkUsers: builder.mutation<unknown, { data: any }>({
      query: ({ data }) => ({
        url: `${API_URL.createBulkUsers}`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result) => {
        if (result)
          return [
            {
              type: "GetDeviceAllocated",
            },
            {
              type: "GetDeviceUnAllocated",
            },
          ];

        return [];
      },
    }),

    addApiToken: builder.mutation<unknown, { data: string }>({
      query: ({ data }) => ({
        url: `${API_URL.addApiToken}`,
        method: "POST",
        body: data,
      }),
      invalidatesTags: (result) => {
        if (result)
          return [
            {
              type: "GetDeviceAllocated",
            },
            {
              type: "GetDeviceUnAllocated",
            },
            {
              type: "GetApiToken",
            },
          ];

        return [];
      },
    }),

    getApiToken: builder.query<GetApiTokenType, void>({
      query: () => ({
        url: `${API_URL.getApiToken}`,
      }),
      providesTags: () => [{ type: "GetApiToken" }],
    }),
  }),
});

export const {
  useGetDeviceListQuery,
  useGetDeviceAllocatedQuery,
  useGetDeviceUnAllocatedQuery,
  useRevokeUserFromDeviceMutation,
  useAllocatedUserInDeviceMutation,
  useStartEnrollmentInDeviceMutation,
  useStopEnrollmentDeviceMutation,
  useCreateBulkUsersMutation,
  useAddApiTokenMutation,
  useGetApiTokenQuery,
} = deviceApi;
